if (!customElements.get('td-shade-finder')) {
  class TdShadeFinder extends HTMLElement {
    static SELECTORS = {
      rendered: '[data-td-rendered]',
      topSwatch: '[data-td-top-swatch]',
      lowerVariantInputs: 'variant-radios input[type="radio"]',
      galleryRoots: 'td-product-gallery, td-variation-gallery, td-variation-gallery-swiper',
    };

    static EVENTS = {
      primaryShadeChange: 'td:primary-shade-change',
    };

    constructor() {
      super();
      this.abortController = null;
      this.variationGalleryStatusCleanup = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handlePrimaryShadeChange = this.handlePrimaryShadeChange.bind(this);
    }

    connectedCallback() {
      this.renderedContainer = this.querySelector(TdShadeFinder.SELECTORS.rendered);
      this.productUrl = this.dataset.tdProductUrl || '';
      this.colorOptionName = this.dataset.tdColorOptionName || '';
      this.remoteSectionId = this.dataset.tdRemoteSectionId || 'product-main';

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener(TdShadeFinder.EVENTS.primaryShadeChange, this.handlePrimaryShadeChange);

      this.hideBrokenSwatchImages();

      if (this.productUrl && this.renderedContainer) {
        this.loadRemoteSection();
      }
    }

    hideBrokenSwatchImages() {
      this.querySelectorAll('.td-shade-finder__swatch-img').forEach((img) => {
        const hide = () => {
          img.style.display = 'none';
        };
        if (img.complete && img.naturalWidth === 0) {
          hide();
        } else {
          img.addEventListener('error', hide, { once: true });
        }
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener(TdShadeFinder.EVENTS.primaryShadeChange, this.handlePrimaryShadeChange);
      if (typeof this.variationGalleryStatusCleanup === 'function') {
        this.variationGalleryStatusCleanup();
        this.variationGalleryStatusCleanup = null;
      }
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    async loadRemoteSection() {
      this.abortController = new AbortController();

      try {
        const renderedHtml = await this.fetchRenderedSectionHtml();
        if (!renderedHtml) {
          throw new Error(`Missing rendered section payload for ${this.remoteSectionId}`);
        }

        const parser = new DOMParser();
        const parsed = parser.parseFromString(renderedHtml, 'text/html');
        parsed.querySelectorAll('.td-product__accordion').forEach((node) => node.remove());
        this.decorateParsedSectionState(parsed);

        await this.installAssets(parsed);

        this.renderedContainer.innerHTML = parsed.body.innerHTML;
        this.decorateRenderedUrls();
        this.executeInlineScripts(this.renderedContainer);
        this.initializeEmbeddedProductModules();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderedContainer.innerHTML = '<div class="td-shade-finder__loading">Unable to load product details.</div>';
        console.error('[td-shade-finder] Failed to load remote product section', error);
      }
    }

    async fetchRenderedSectionHtml() {
      const configuredSectionId = this.remoteSectionId;
      let renderedHtml = await this.requestRenderedSectionHtml(configuredSectionId);

      if (renderedHtml || configuredSectionId !== 'product-main') {
        return renderedHtml;
      }

      const discoveredSectionId = await this.discoverRemoteSectionId();
      if (!discoveredSectionId || discoveredSectionId === configuredSectionId) {
        return renderedHtml;
      }

      this.remoteSectionId = discoveredSectionId;
      return this.requestRenderedSectionHtml(discoveredSectionId);
    }

    async requestRenderedSectionHtml(sectionId) {
      const response = await fetch(this.buildSectionUrl(sectionId), {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load section: ${response.status}`);
      }

      const responseText = await response.text();
      const payload = JSON.parse(responseText);
      return payload?.[sectionId] || '';
    }

    async discoverRemoteSectionId() {
      const response = await fetch(this.buildProductPageUrl(), {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to discover product page section: ${response.status}`);
      }

      const responseText = await response.text();
      const parser = new DOMParser();
      const parsed = parser.parseFromString(responseText, 'text/html');
      const productMain = parsed.querySelector('.td-product-main[data-section]');

      if (!productMain) {
        throw new Error('Unable to find td-product-main section in product page response');
      }

      const sectionId = productMain.dataset.section;

      if (!sectionId) {
        throw new Error('Found td-product-main but it has no section id');
      }

      return sectionId;
    }

    async installAssets(parsedDocument) {
      const loadPromises = [];

      parsedDocument.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
        const href = link.getAttribute('href');
        if (!href || this.headAssetExists('link', href)) return;

        const clone = document.createElement('link');
        clone.rel = 'stylesheet';
        clone.href = href;
        document.head.appendChild(clone);
      });

      parsedDocument.querySelectorAll('script[src]').forEach((script) => {
        const src = script.getAttribute('src');
        if (!src) return;

        if (this.headAssetExists('script', src)) return;

        const clone = document.createElement('script');
        clone.src = src;
        clone.defer = script.defer;
        clone.async = script.async;
        if (script.type) {
          clone.type = script.type;
        }

        loadPromises.push(new Promise((resolve, reject) => {
          clone.addEventListener('load', resolve, { once: true });
          clone.addEventListener('error', reject, { once: true });
        }));

        document.head.appendChild(clone);
      });

      parsedDocument.querySelectorAll('link[rel="stylesheet"], script[src]').forEach((node) => node.remove());

      await Promise.all(loadPromises);
    }

    decorateParsedSectionState(parsedDocument) {
      if (!this.remoteSectionId) return;

      parsedDocument
        .querySelectorAll('variant-radios, variant-selects, product-info')
        .forEach((node) => {
          node.dataset.originalSection = this.remoteSectionId;
          if (node.tagName === 'VARIANT-RADIOS' || node.tagName === 'VARIANT-SELECTS') {
            node.dataset.updateUrl = 'false';
          }
        });
    }

    headAssetExists(tagName, assetUrl) {
      const normalizedAssetUrl = new URL(assetUrl, window.location.origin).href;

      return Array.from(document.head.querySelectorAll(`${tagName}[href], ${tagName}[src]`)).some((node) => {
        const currentUrl = node.getAttribute('href') || node.getAttribute('src');
        if (!currentUrl) return false;

        return new URL(currentUrl, window.location.origin).href === normalizedAssetUrl;
      });
    }

    isShadeFinderExecutableScript(script) {
      if (!script || script.src || script.type === 'application/json') return false;
      if (script.hasAttribute('data-td-safe')) return true;

      const scriptContent = script.textContent || '';
      return [
        "customElements.define('td-product-gallery'",
        "customElements.define('td-variation-gallery'",
        "customElements.define('td-variation-gallery-swiper'",
      ].some((pattern) => scriptContent.includes(pattern));
    }

    executeInlineScripts(container) {
      container.querySelectorAll('script:not([src])').forEach((script) => {
        if (!this.isShadeFinderExecutableScript(script)) return;
        const replacement = document.createElement('script');
        Array.from(script.attributes).forEach((attribute) => {
          replacement.setAttribute(attribute.name, attribute.value);
        });
        replacement.textContent = script.textContent;
        script.replaceWith(replacement);
      });
    }

    initializeEmbeddedProductModules() {
      window.requestAnimationFrame(() => {
        this.renderedContainer
          .querySelectorAll('swiper-slider')
          .forEach((slider) => {
            if (typeof slider.initializeSwiper === 'function') {
              slider.initializeSwiper();
            }
          });

        this.renderedContainer
          .querySelectorAll('variant-radios, variant-selects')
          .forEach((picker) => {
            const sectionId = picker.dataset.section;
            if (!sectionId) return;

            const mediaGallery = this.renderedContainer.querySelector(`[id^="MediaGallery-${sectionId}"]`);
            if (!mediaGallery) return;

            picker.mediaGallery = mediaGallery;
            picker.imagesWrapper = mediaGallery.querySelector('[data-product-images]');
            picker.images = picker.imagesWrapper
              ? Array.from(picker.imagesWrapper.querySelectorAll('[data-product-image]'))
              : [];
            picker.thumbsWrapper = mediaGallery.querySelector('.swiper-thumbs .swiper-wrapper');
            picker.thumbs = picker.thumbsWrapper
              ? Array.from(picker.thumbsWrapper.querySelectorAll('[data-product-image]'))
              : [];

            if (typeof picker.updateGallery === 'function') {
              picker.updateGallery(true);
            }

            if (typeof picker.updateFinalPrice === 'function') {
              picker.updateFinalPrice();
            }
          });

        this.initializeVariationGalleryStatus();
        this.syncTopSwatchesFromLower();
      });
    }

    buildProductPageUrl() {
      return new URL(this.withPreviewParams(this.productUrl), window.location.origin).toString();
    }

    buildSectionUrl(sectionId = this.remoteSectionId) {
      const url = new URL(this.buildProductPageUrl(), window.location.origin);
      url.searchParams.set('sections', sectionId);
      return url.toString();
    }

    withPreviewParams(pathOrUrl) {
      const url = new URL(pathOrUrl, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);

      ['preview_theme_id', 'pb', '_fd'].forEach((param) => {
        if (currentParams.has(param) && !url.searchParams.has(param)) {
          url.searchParams.set(param, currentParams.get(param));
        }
      });

      return `${url.pathname}${url.search}`;
    }

    decorateRenderedUrls() {
      if (!this.renderedContainer) return;

      this.renderedContainer.querySelectorAll('[data-url]').forEach((node) => {
        const currentUrl = node.getAttribute('data-url');
        if (!currentUrl) return;
        node.setAttribute('data-url', this.withPreviewParams(currentUrl));
      });
    }

    handleClick(event) {
      const topSwatch = event.target.closest(TdShadeFinder.SELECTORS.topSwatch);
      if (!topSwatch) return;

      const shadeValue = topSwatch.dataset.tdShadeValue;
      if (!shadeValue) return;

      this.selectLowerShade(shadeValue);
    }

    handleChange(event) {
      if (!this.colorOptionName) return;

      const lowerInput = event.target.closest('input[type="radio"]');
      if (!lowerInput) return;
      if (lowerInput.name !== this.colorOptionName) return;

      this.updateTopSwatches(lowerInput.value);
      const picker = lowerInput.closest('variant-radios, variant-selects');
      if (picker) {
        this.syncVariationGallery(picker);
      }
    }

    handlePrimaryShadeChange(event) {
      const shadeValue = event.detail?.shadeValue;
      const inputName = event.detail?.inputName;

      if (!shadeValue) return;
      if (inputName && this.colorOptionName && inputName !== this.colorOptionName) return;

      this.selectLowerShade(shadeValue);
    }

    selectLowerShade(shadeValue) {
      if (!this.colorOptionName || !this.renderedContainer) return;

      const lowerInputs = Array.from(this.renderedContainer.querySelectorAll(TdShadeFinder.SELECTORS.lowerVariantInputs));
      const target = lowerInputs.find((input) => input.name === this.colorOptionName && input.value === shadeValue);

      if (!target) return;

      if (target.checked) {
        this.updateTopSwatches(target.value);
        const picker = target.closest('variant-radios, variant-selects');
        if (picker) {
          this.syncVariationGallery(picker);
        }
        return;
      }

      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncTopSwatchesFromLower() {
      if (!this.colorOptionName || !this.renderedContainer) return;

      const checkedInput = Array.from(this.renderedContainer.querySelectorAll(TdShadeFinder.SELECTORS.lowerVariantInputs)).find((input) => {
        return input.name === this.colorOptionName && input.checked;
      });

      if (!checkedInput) return;
      this.updateTopSwatches(checkedInput.value);
      const picker = checkedInput.closest('variant-radios, variant-selects');
      if (picker) {
        this.syncVariationGallery(picker);
      }
    }

    updateTopSwatches(activeValue) {
      this.querySelectorAll(TdShadeFinder.SELECTORS.topSwatch).forEach((swatch) => {
        const isActive = swatch.dataset.tdShadeValue === activeValue;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    findRenderedGalleryRoot() {
      return this.renderedContainer?.querySelector(TdShadeFinder.SELECTORS.galleryRoots) || null;
    }

    findParsedGalleryRoot(parsedDocument, sectionId) {
      if (!parsedDocument || !sectionId) return null;
      return parsedDocument.querySelector(
        `td-product-gallery[data-section="${sectionId}"], td-variation-gallery[data-section="${sectionId}"], td-variation-gallery-swiper[data-section="${sectionId}"]`
      );
    }

    initializeVariationGalleryStatus(galleryRoot = null) {
      const variationGallery = galleryRoot?.matches('td-variation-gallery-swiper')
        ? galleryRoot
        : this.renderedContainer?.querySelector('td-variation-gallery-swiper');

      if (!variationGallery) return;

      const sliderElement = variationGallery.querySelector('swiper-slider');
      const currentElement = variationGallery.querySelector('[data-td-current]');
      const totalElement = variationGallery.querySelector('[data-td-total]');

      if (!sliderElement || !currentElement) return;

      if (typeof this.variationGalleryStatusCleanup === 'function') {
        this.variationGalleryStatusCleanup();
        this.variationGalleryStatusCleanup = null;
      }

      const updateStatus = (swiperInstance = sliderElement.slider) => {
        if (!swiperInstance) return;

        currentElement.textContent = `${swiperInstance.activeIndex + 1}`;

        if (totalElement && swiperInstance.slides?.length) {
          totalElement.textContent = `${swiperInstance.slides.length}`;
        }
      };

      const bindStatus = (swiperInstance) => {
        if (!swiperInstance) return;

        const handleSlideChange = () => updateStatus(swiperInstance);
        if (typeof swiperInstance.on === 'function') {
          swiperInstance.on('slideChange', handleSlideChange);
        }

        this.variationGalleryStatusCleanup = () => {
          sliderElement.removeEventListener('swiper:ready', handleReady);
          if (typeof swiperInstance.off === 'function') {
            swiperInstance.off('slideChange', handleSlideChange);
          }
        };

        updateStatus(swiperInstance);
      };

      const handleReady = (event) => {
        bindStatus(event.detail?.swiper);
      };

      sliderElement.addEventListener('swiper:ready', handleReady, { once: true });

      if (sliderElement.slider) {
        bindStatus(sliderElement.slider);
      }
    }

    async syncVariationGallery(picker) {
      if (!picker?.currentVariant?.id) return;
      if (!this.remoteSectionId) return;

      const destinationGalleryRoot = this.findRenderedGalleryRoot();
      if (!destinationGalleryRoot) return;

      const sourceUrl = this.withPreviewParams(picker.dataset.url || this.productUrl);
      const requestUrl = new URL(sourceUrl, window.location.origin);
      requestUrl.searchParams.set('variant', picker.currentVariant.id);
      const sourceSectionId = picker.dataset.originalSection || picker.dataset.section || this.remoteSectionId;
      requestUrl.searchParams.set('section_id', sourceSectionId);

      try {
        const response = await fetch(requestUrl.toString());
        if (!response.ok) return;

        const responseText = await response.text();
        const parser = new DOMParser();
        const parsed = parser.parseFromString(responseText, 'text/html');
        const sourceGalleryRoot = this.findParsedGalleryRoot(parsed, sourceSectionId);

        if (!sourceGalleryRoot) return;

        const replacementGalleryRoot = sourceGalleryRoot.cloneNode(true);
        destinationGalleryRoot.replaceWith(replacementGalleryRoot);

        replacementGalleryRoot.querySelectorAll('swiper-slider').forEach((slider) => {
          if (typeof slider.initializeSwiper === 'function') {
            slider.initializeSwiper();
          }
        });

        this.initializeVariationGalleryStatus(replacementGalleryRoot);
      } catch (error) {
        console.error('[td-shade-finder] Failed to sync variation gallery', error);
      }
    }
  }

  customElements.define('td-shade-finder', TdShadeFinder);
}
